/**
 * One place that turns an instant into text a human reads.
 *
 * WHY THIS EXISTS. Every renderer used to reach for UTC getters
 * (`getUTCHours()` and friends) and label the result "UTC". The stated reason
 * was determinism: a formatter that follows the host's local clock produces a
 * different string on a developer's laptop than in CI, and the tests flake.
 * That reasoning was right about the hazard and wrong about the remedy —
 * `Intl.DateTimeFormat` with an EXPLICIT `timeZone` is exactly as deterministic
 * as a UTC getter, because neither consults the host zone. So we get a stable
 * test and a timestamp in the operator's own zone; there was never a trade-off
 * to make.
 *
 * The zone is configuration (`v.tz`), not a constant, and it is passed in
 * rather than read from a global: these functions stay pure, which is what lets
 * a test pin a zone without touching process state.
 */
/**
 * Mountain time. Chosen as the default because that is where we operate, and a
 * timestamp is far more often read by us than by anyone else. Overridable per
 * deployment via the `v.tz` setting.
 */
export const TIMEZONE_DEFAULT = 'America/Denver';
/**
 * Offered in the settings dropdown. Deliberately short: the US zones we
 * actually work across, plus UTC as the escape hatch for anyone who genuinely
 * wants raw instants. Any valid IANA name works if set directly — this list is
 * a convenience, not a validator.
 */
export const TIMEZONE_OPTIONS = [
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Chicago',
    'America/New_York',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/**
 * Pull the calendar parts of an instant AS SEEN IN `timeZone`.
 *
 * Returns null for anything unparsable, and also for an unusable zone: an
 * invalid IANA name makes `Intl.DateTimeFormat` throw, and a timestamp is never
 * worth taking a renderer down for. Callers degrade to the raw ISO string,
 * which is ugly but honest — the one thing none of them do is print
 * "Invalid Date".
 */
function partsInZone(iso, timeZone) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return null;
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: 'numeric',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        }).formatToParts(d);
    }
    catch {
        return null;
    }
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    // hour12:false yields "24" for midnight in some engines rather than "00".
    const rawHour = get('hour');
    const hour = rawHour === '24' ? '00' : rawHour;
    return {
        year: get('year'),
        month: Number(get('month')) - 1,
        day: get('day'),
        hour,
        minute: get('minute'),
        second: get('second'),
        abbr: get('timeZoneName'),
    };
}
/**
 * "2026-08-05 18:31:46 MDT" — the form written into a Halo note.
 *
 * Carries the zone abbreviation on purpose. A note is read months later by
 * someone who was not in the conversation, and a bare wall-clock time with no
 * zone is a fact you cannot check.
 */
export function formatStampInZone(iso, timeZone) {
    const p = partsInZone(iso, timeZone);
    if (!p)
        return iso;
    const month = String(p.month + 1).padStart(2, '0');
    return `${p.year}-${month}-${p.day} ${p.hour}:${p.minute}:${p.second} ${p.abbr}`;
}
/** "18:31" — a transcript line, where the surrounding context already gives the day. */
export function formatHmInZone(iso, timeZone) {
    const p = partsInZone(iso, timeZone);
    if (!p)
        return iso;
    return `${p.hour}:${p.minute}`;
}
/** "05 Aug, 18:31" — a closed-thread row, where the year is noise but the day is not. */
export function formatDayHmInZone(iso, timeZone) {
    const p = partsInZone(iso, timeZone);
    if (!p)
        return null;
    return `${p.day} ${MONTHS[p.month]}, ${p.hour}:${p.minute}`;
}
/** "05 Aug 2026, 18:31 MDT" — the READ ONLY banner, the one place the full date earns its space. */
export function formatOpenedAtInZone(iso, timeZone) {
    const p = partsInZone(iso, timeZone);
    if (!p)
        return iso;
    return `${p.day} ${MONTHS[p.month]} ${p.year}, ${p.hour}:${p.minute} ${p.abbr}`;
}
